import { CssBaseline, GlobalStyles, ThemeProvider, alpha, createTheme, darken, lighten } from '@mui/material';
import { get, isEmpty } from 'lodash';
import React, { useEffect, lazy, Suspense, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { connect } from 'react-redux';
import { settingsActions } from '../store/reducers/settings';
import theme from '../theme/index';
import MainLayout from '../common/MainLayout';
import { SnackbarProvider } from 'notistack';
import { components } from '../theme/components';
import { typography } from '../theme/typography';
import { useState } from 'react';
import lightOrdarkColor from '@check-light-or-dark/color';
import { defaultPageSetting } from '../pages/Settings/form';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingScreen from './loadingScreen';
import { init, loadRemote } from '@module-federation/runtime';
import ReactDOM from "react-dom";
import { InitializeRemoteApp } from './InitializeRemoteApp';
import { useCaseBuilderActions } from '../store/reducers/usecase-management';
import { menuList } from '../utils/route-paths';

//'https://fonts.googleapis.com/css?family=Josefin+Sans';
declare module '@mui/material/styles' {
  interface Palette {
    custom: Palette['primary'];
    customBorderColor: Palette['primary'];
  }

  interface PaletteOptions {
    custom?: PaletteOptions['primary'];
    customBorderColor?: PaletteOptions['primary'];
  }
}
const LazyMainLayout = lazy(() => import('../common/MainLayout'));

const ThemeProviderLayout = ({ children, settingsData, isScreenLoad, menuData, userDetails, userAccess }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);
  const [updatedMenuData, setUpdatedMenuData] = useState(menuData);
  const [defaultDashboard, setDefaultDashboard] = useState<any>(null);
  const [isDataReady, setIsDataReady] = useState(false);

  const themeMode: any = !isEmpty(settingsData)
    ? {
      themeMode: lightOrdarkColor(get(settingsData.config, 'background_color', '#F8FAFB')),
      isLightBG: lightOrdarkColor(get(settingsData.config, 'background_color', '#F8FAFB')) === 'light',
      isLightNav: lightOrdarkColor(get(settingsData.config, 'secondary_color', '#D9DFED')) === 'light',
    }
    : {
      themeMode: lightOrdarkColor(get(defaultPageSetting.config, 'background_color', '#F8FAFB')),
      isLightBG: true,
      isLightNav: true,
    };

  useEffect(() => {
    const payload: any = { filter_status: 'deployed' }
    const fetchData = async () => {
      await Promise.all([
        dispatch(settingsActions.getSettingsDataRequest('')),
        dispatch(useCaseBuilderActions.getDeployedUsecaseRequest(payload)),
      ]);
    };

    fetchData();
  }, [dispatch]);

  const lightTheme = !isEmpty(settingsData)
    ? createTheme({
      typography: { ...typography },
      palette: {
        primary: {
          main: get(settingsData.config, 'primary_color', '#5C85FF'),
        },
        secondary: {
          main: get(settingsData.config, 'secondary_color', '#FFFFFF'),
        },
        text: {
          primary: get(settingsData.config, 'text_color', '#434D67'),
          secondary: lighten(get(settingsData.config, 'text_color', '#434D67'), 0.1), //#64759F
          disabled: lighten(get(settingsData.config, 'text_color', '#434D67'), 0.6),
        },
        background: {
          default: get(settingsData.config, 'background_color', '#F8FAFB'),
          paper: lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.8),
        },
        /***{
           * main : tooltip color
           * light : icon color
           * dark :
           * contrastText :
          } */
        custom: {
          main:
            lightOrdarkColor(get(settingsData.config, 'text_color', '#434D67')) === 'light'
              ? lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.2)
              : lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.2),
          light: lighten(get(settingsData.config, 'text_color', '#434D67'), 0.1),
        },
        customBorderColor: {
          main: themeMode.isLightBG
            ? darken(get(settingsData.config, 'background_color', '#F8FAFB'), 0.1)
            : lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.1),
        },
        success: {
          main: '#0EAB4E',
        },
        error: {
          main: '#D81010',
        },
      },
      components: {
        ...components,

        //  MuiCssBaseline: {
        //     styleOverrides: {
        //         '::-webkit-scrollbar:: ': {
        //             width: '8px',
        //             height: '8px'
        //         },

        //         '::-webkit-scrollbar-thumb:hover': {
        //             background: '#00000'
        //         },

        //         '::-webkit-scrollbar-thumb': {
        //             background: '#A6B5D8',
        //             borderRradius: '16px',
        //             borderBottomWidth: '100px',
        //         },

        //         '.modalDialogContent': {
        //             ' & ::-webkit-scrollbar-track': {
        //                 margin: '12px'
        //             }
        //         }

        //     }
        // }
      },
    })
    : createTheme({
      typography: { ...typography },
      palette: {
        primary: {
          main: get(defaultPageSetting.config, 'primary_color', '#5C85FF'),
          light: lighten(get(defaultPageSetting.config, 'primary_color', '#5C85FF'), 0.8),
        },
        secondary: {
          main: get(defaultPageSetting.config, 'secondary_color', '#FFFFFF'),
        },
        text: {
          primary: get(defaultPageSetting.config, 'text_color', '#434D67'),
          secondary: lighten(get(defaultPageSetting.config, 'text_color', '#434D67'), 0.23), //#64759F
          disabled: lighten(get(defaultPageSetting.config, 'text_color', '#434D67'), 0.6),
        },
        background: {
          default: get(defaultPageSetting.config, 'background_color', '#F8FAFB'),
          paper: lighten(get(defaultPageSetting.config, 'background_color', '#F8FAFB'), 0.8),
        },
        /***{
           * main : tooltip color
           * light : icon color
           * dark :
           * contrastText :
          } */
        custom: {
          main:
            lightOrdarkColor(get(defaultPageSetting.config, 'text_color', '#434D67')) === 'light'
              ? lighten(get(defaultPageSetting.config, 'background_color', '#F8FAFB'), 0.2)
              : lighten(get(defaultPageSetting.config, 'background_color', '#F8FAFB'), 0.2),
          light: lighten(get(defaultPageSetting.config, 'text_color', '#434D67'), 0.2), //((lightOrdarkColor(get(settingsData.config, 'background_color', '#F8FAFB')) === 'light' && lightOrdarkColor(get(settingsData.config, 'secondary_color', '#D9DFED')) === 'light') || (lightOrdarkColor(get(settingsData.config, 'background_color', '#F8FAFB')) === 'dark' && lightOrdarkColor(get(settingsData.config, 'secondary_color', '#D9DFED')) === 'dark')) ? lighten(get(settingsData.config, 'text_color', '#434D67'), 0.2) : (lightOrdarkColor(get(settingsData.config, 'background_color', '#F8FAFB')) === 'dark' && lightOrdarkColor(get(settingsData.config, 'secondary_color', '#D9DFED')) === 'light') ? lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.3) : lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.2),
        },
        customBorderColor: {
          main: themeMode.isLightBG
            ? darken(get(settingsData.config, 'background_color', '#F8FAFB'), 0.1)
            : lighten(get(settingsData.config, 'background_color', '#F8FAFB'), 0.1),
        },
      },
      components: { ...components },
    });


  const loadChildSubMenu = async (applicationUrl: string, applicationName: string, componentName: string) => {
    try {
      const submenuModule: any = await loadRemote(`${applicationName}/${componentName}`);
      if (submenuModule && submenuModule.getSubMenu) {
        return submenuModule.getSubMenu(); // Fetch submenu data
      } else {
        console.warn(`Submenu component not found for ${applicationName}/${componentName}`);
        return []; // Return an empty array if the submenu component is not found
      }
    } catch (error) {
      console.error(`Failed to load submenu for ${applicationName}:`, error);
      return []; // Return an empty array if there is an error
    }
  };

  const hasMenuAccess = (menuItem: any, userDetails: any) => {
    const accessConfig = menuItem.user_access_config;
    if (!accessConfig) return undefined; // undefined means "no explicit access config"
    const userRoleIds = (userDetails.roles || []).map((role: any) => role.id);
    if (Array.isArray(accessConfig.roles) && accessConfig.roles.some((roleId: string) => userRoleIds.includes(roleId))) {
      return true;
    }
    if (Array.isArray(accessConfig.users) && accessConfig.users.includes(userDetails.id)) {
      return true;
    }
    return false;
  };

  const checkUserAccess = (path: string, endPointPath: string) => {
    switch (path) {
      case '/user-management':
        return !(get(userAccess, `auth/users.read`, false) || get(userAccess, `auth/roles.read`, false));
      // case '/connectivity-dashboard':
      //   return !get(userAccess, `site/master.read`, false);
      case '/gpt':
      case '/emsaver':
        return false;
      default:
        return !get(userAccess, `[${endPointPath}].read`, false);
    }
  };

  const updateMenuDataWithSubMenu = async (menuData: any[], userData: any) => {
    let foundDefaultDashboard: any = null;

    const processMenu = async (menuList: any[]): Promise<any[]> => {
      const result: any[] = [];
      for (const menuItem of menuList) {
        // --- Application type (keep for future use) ---
        // if (menuItem.type === 'application') {
        //   const { applicationUrl, applicationName, componentName } = menuItem;
        //   try {
        //     await InitializeRemoteApp(applicationUrl, applicationName);
        //     const subMenu = await loadChildSubMenu(applicationUrl, applicationName, 'SubMenu');
        //     if (subMenu.length > 0) {
        //       result.push({
        //         ...menuItem,
        //         haveSubMenu: true,
        //         children: subMenu.map((child: any) => ({
        //           ...child,
        //           isSubMenu: true,
        //         })),
        //       });
        //       continue;
        //     }
        //   } catch (error) {
        //     console.error(`Error initializing or loading submenu for ${applicationName}:`, error);
        //   }
        //   result.push(menuItem);
        //   continue;
        // }

        // --- Dashboard type ---
        if (menuItem.type === 'dashboard' && Array.isArray(menuItem.children)) {

          const userId = userData?.id;
          let filteredChildren = menuItem.children.filter((child: any) =>
            hasMenuAccess(child, userData)
          );

          const staticDashboard = {
            id: 1,
            title: 'Dashboard',
            type: 'dashboard',
            Icon: 'Dashboard',
            path: '/dashboard',
            route_id: 'dashboard',
            isSubMenu: true,
            isDefault: false,
          };

          if (process.env.REACT_APP_DEFAULT_DASHBOARD === 'enabled') {
            filteredChildren = [staticDashboard, ...filteredChildren];
          } else {
            if (menuItem.children.length === 0) {
              filteredChildren = [{ ...staticDashboard, path: '/dashboard/create', isDefault: true }];
            }
            if (menuItem.children.length > 0 && filteredChildren.length === 0) {
              filteredChildren = [{ ...staticDashboard, path: '/dashboard/access-denied', isDefault: true }];
            }
          }

          // let updatedChildren = filteredChildren;
          let updatedChildren = filteredChildren.map((child: any) => ({
            ...child,
            isDefault: Array.isArray(child.default_dashboard_users) && child.default_dashboard_users.includes(userId),
          }));

          if (!updatedChildren.some((child: any) => child.isDefault) && updatedChildren.length > 0) {
            updatedChildren = updatedChildren.map((child: any, idx: number) =>
              idx === 0 ? { ...child, isDefault: true } : child
            );
          }

          if (!foundDefaultDashboard && updatedChildren.length > 0) {
            foundDefaultDashboard = updatedChildren.find((child: any) => child.isDefault) || updatedChildren[0];
          }

          result.push(
            updatedChildren.length === 0
              ? { ...menuItem, hidden: true, children: [] }
              : { ...menuItem, children: updatedChildren }
          );
          continue;
        }

        // --- All other types with children ---
        if (Array.isArray(menuItem.children) && menuItem.type !== 'dashboard') {
          const updatedChildren: any[] = [];
          for (const child of menuItem.children) {
            if (checkUserAccess(child.path, child.endPointPath)) {
              updatedChildren.push({ ...child, hidden: true });
            } else if (Array.isArray(child.children)) {
              updatedChildren.push({ ...child, children: await processMenu(child.children) });
            } else {
              updatedChildren.push(child);
            }
          }
          const allChildrenHidden = updatedChildren.length > 0 && updatedChildren.every((child: any) => child.hidden);
          result.push({
            ...menuItem,
            ...(allChildrenHidden ? { hidden: true } : {}),
            children: updatedChildren,
          });
          continue;
        }
        // --- Fallback for items with children but no type check ---
        if (Array.isArray(menuItem.children)) {
          result.push({
            ...menuItem,
            children: await processMenu(menuItem.children),
          });
          continue;
        }

        // --- Leaf node ---
        result.push(menuItem);
      }
      return result;
    };

    const updatedMenu = await processMenu(menuData);
    return { menu: updatedMenu, defaultDashboard: foundDefaultDashboard };
  };

  useEffect(() => {
    if (!userDetails || isEmpty(userDetails) || menuData.length === 0) return;

    const fetchMenuData = async () => {
      try {
        const { menu, defaultDashboard } = await updateMenuDataWithSubMenu(menuData, userDetails);
        setUpdatedMenuData(menu);
        setDefaultDashboard(defaultDashboard);
      } catch (error) {
        setUpdatedMenuData(menuData); // Fallback to the static menuData from the reducer
      } finally {
        setIsDataReady(true); // Ensure the UI loads regardless of success or failure
      }
    };

    fetchMenuData();
  }, [menuData, userDetails]);

  useEffect(() => {
    if (
      !hasRedirected.current &&
      isScreenLoad &&
      isDataReady &&
      (location.pathname === '/' || location.pathname === '/dashboard')
    ) {
      if (defaultDashboard && defaultDashboard.grafana_uid) {
        hasRedirected.current = true;
        navigate(`${defaultDashboard.path}`, { replace: true });
      } else {
        hasRedirected.current = true;
        const firstAccessiblePath = getFirstAccessibleChildPath(updatedMenuData);
        if (firstAccessiblePath) {
          navigate(`${firstAccessiblePath}`, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    }
  }, [isScreenLoad, isDataReady, defaultDashboard, location.pathname, navigate]);

  useEffect(() => {
    if (hasRedirected.current) {
      if (!hasAccessToCurrentPath(updatedMenuData, location.pathname)) {
        navigate('/notauthorized', { replace: true })
      }
    }
  }, [hasRedirected.current])

  const hasAccessToCurrentPath: any = (menuList: any[], currentPath: string): boolean => {
    for (const item of menuList) {
      if (item.path === currentPath && item.hidden !== true) {
        return true;
      }
      if (Array.isArray(item.children)) {
        if (hasAccessToCurrentPath(item.children, currentPath)) {
          return true;
        }
      }
    }
    return false;
  };

  const getFirstAccessibleChildPath = (menuList: any[]): string | null => {
    for (const item of menuList) {
      if (item.hidden !== true && item.path) {
        return item.path;
      }
      if (Array.isArray(item.children)) {
        const childPath = getFirstAccessibleChildPath(item.children);
        if (childPath) return childPath;
      }
    }
    return null;
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <GlobalStyles
        styles={{
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: '#868686',
          },
          '*::-webkit-scrollbar-thumb': {
            background: '#A3A3A3',
            borderRadius: '16px',
            borderBottomWidth: '100px',
          },
        }}
      />
      <SnackbarProvider>
        {isScreenLoad && isDataReady ? (
          <Suspense fallback={<LoadingScreen />}>
            <LazyMainLayout themeMode={themeMode} menuData={updatedMenuData} />
          </Suspense>
        ) : (
          <LoadingScreen />
        )}
      </SnackbarProvider>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: any) => ({
  settingsData: state.settings.settingsData,
  isScreenLoad: state.settings.isScreenLoad,
  menuData: state.useCaseBuilder.mergedMenuData,
  userDetails: state.profile.userDetails,
  userAccess: state.profile.userAccess,
});
export default connect(mapStateToProps)(ThemeProviderLayout);
